import { Component, Injector } from '@angular/core';
import { appModuleAnimation } from '@shared/animations/routerTransition';
import { PagedCommonResultRequestDto, PagedListingComponentBase } from '@shared/paged-listing-component-base';
import { EventDto, EventDtoPagedResultDto, EventServiceProxy, EventTypeEnum, CompanyServiceProxy, NaturalPersonServiceProxy, JuridicalPersonServiceProxy } from '@shared/service-proxies/service-proxies';
import * as moment from 'moment';
import { finalize } from "rxjs";
import { CustomModalService } from '@shared/helpers/custom-modal.service';
import { CreateOrEditReminderComponent } from '@app/reminders/create-or-edit-reminder/create-or-edit-reminder.component';

@Component({
  selector: 'app-events',
  templateUrl: './events.component.html',
  styleUrl: './events.component.css',
  animations: [appModuleAnimation()],
})
export class EventsComponent extends PagedListingComponentBase<EventDto> {

  icon: string = "event";
  title: string = this.l("Events");

  keyword: string;
  showingFilters = false;
  loading = false;
  isResizing = false;

  fromMonth: Date | undefined;
  toMonth: Date | undefined;
  selectedEventTypes: EventTypeEnum[] = [];
  selectedCompanies: number[] = [];
  selectedNaturalPersons: number[] = [];
  selectedJuridicalPersons: number[] = [];

  companyOptions: { label: string, value: number }[] = [];
  naturalPersonOptions: { label: string, value: number }[] = [];
  juridicalPersonOptions: { label: string, value: number }[] = [];

  columns = [
    { field: 'name', header: this.l('Description'), visible: true },
    { field: 'date', header: this.l('Date'), visible: true },
    { field: 'type', header: this.l('Type'), visible: true },
  ];

  events: EventDto[] = [];

  constructor(
    injector: Injector,
    private _eventService: EventServiceProxy,
    private _companyService: CompanyServiceProxy,
    private _naturalPersonService: NaturalPersonServiceProxy,
    private _juridicalPersonService: JuridicalPersonServiceProxy,
    private _modalService: CustomModalService
  ) {
    super(injector);
    this.loadFilterOptions();
  }

  protected list(request: PagedCommonResultRequestDto, pageNumber: number, finishedCallback: Function): void {
    this._eventService
      .getAllEvents(
        this.fromMonth ? moment(this.fromMonth) : undefined,
        this.toMonth ? moment(this.toMonth) : undefined,
        this.selectedEventTypes.length >= 1 ? this.selectedEventTypes : undefined,
        this.selectedCompanies.length >= 1 ? this.selectedCompanies : undefined,
        this.selectedNaturalPersons.length >= 1 ? this.selectedNaturalPersons : undefined,
        this.selectedJuridicalPersons.length >= 1 ? this.selectedJuridicalPersons : undefined,
        this.orderField,
        this.getOrderDirectionEnum(this.orderDirection),
        request.keyword,
        this.filters,
        request.skipCount,
        request.maxResultCount
      )
      .pipe(
        finalize(() => {
          finishedCallback();
        })
      )
      .subscribe((result: EventDtoPagedResultDto) => {
        this.events = result.items;
        this.showPaging(result, pageNumber);
      });
  }

  protected delete(entity: EventDto): void {
  }

  showFilters(): void {
    this.showingFilters = !this.showingFilters;
  }

  onSort(event: any): void {
    this.orderField = event.sortField;
    this.orderDirection = event.sortOrder;

    this.getDataPage({
      first: (this.pageNumber - 1) * this.pageSize,
      rows: this.pageSize,
      sortField: this.orderField,
      sortOrder: this.orderDirection
    });
  }

  getDataPage(event: any): void {
    if (event.first == undefined) {
      event = {
        first: (this.pageNumber - 1) * this.pageSize,
        rows: this.pageSize,
        sortField: this.orderField,
        sortOrder: this.orderDirection
      };
    }

    this.prepareFilters();
    this.pageNumber = event.first / event.rows + 1;

    this.orderField = event.sortField ?? this.orderField;
    this.orderDirection = event.sortOrder ?? this.orderDirection;

    const request = {
      skipCount: event.first || 0,
      maxResultCount: event.rows,
      keyword: this.keyword || ''
    };

    this.loading = true;
    this.list(request, this.pageNumber, () => {
      this.loading = false;
    });
  }

  prepareFilters(): void {
    const filters: any[] = [];

    filters.push({
      fieldName: 'VisibleColumns',
      value: this.visibleColumns.map(c => c.field).join(','),
      fieldType: 1,
      fieldComparator: 0
    });

    if (this.fromMonth) {
      filters.push({
        fieldName: 'FromMonth',
        value: this.fromMonth.toISOString(),
        fieldType: 1,
        fieldComparator: 0
      });
    }

    if (this.toMonth) {
      filters.push({
        fieldName: 'ToMonth',
        value: this.toMonth.toISOString(),
        fieldType: 1,
        fieldComparator: 0
      });
    }

    this.filters = filters;
  }

  onFilterChange(): void {
    this.getDataPage(1);
  }

  onToMonthChange(selectedDate: Date): void {
    if (selectedDate) {
      const lastDayOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
      this.toMonth = lastDayOfMonth;
    } else {
      this.toMonth = undefined;
    }
    this.onFilterChange();
  }

  onColumnDrop(event: any): void {
    const visibleCols = this.visibleColumns;
    const draggedCol = visibleCols[event.previousIndex];
    const targetCol = visibleCols[event.currentIndex];

    const prevRealIndex = this.columns.findIndex(c => c.field === draggedCol?.field);
    const currRealIndex = this.columns.findIndex(c => c.field === targetCol?.field);

    if (prevRealIndex !== -1 && currRealIndex !== -1 && prevRealIndex !== currRealIndex) {
      const movedColumn = this.columns.splice(prevRealIndex, 1)[0];
      this.columns.splice(currRealIndex, 0, movedColumn);
    }
  }

  toggleColumn(col): void {
    const visibleCount = this.columns.filter(c => c.visible).length;
    if (col.visible && visibleCount === 1) {
      abp.message.warn(this.l("AtLeastOneColumnVisible"));
      return;
    }
    col.visible = !col.visible;
    this.applyResizeListeners();

    // Force column redistribution immediately
    setTimeout(() => {
      this.redistributeColumnWidths();
    }, 50);
  }

  redistributeColumnWidths(): void {
    const table = document.querySelector('.p-datatable-table') as HTMLElement;
    if (!table) return;

    const tableId = table.id;
    if (!tableId) return;

    const headerCells = table.querySelectorAll('thead th') as NodeListOf<HTMLElement>;
    const visibleHeaders = Array.from(headerCells).filter(th =>
      th.style.display !== 'none' && !th.classList.contains('aux-col') && !th.classList.contains('actions-col')
    );

    if (visibleHeaders.length === 0) return;

    const auxColWidth = 4;
    const actionsColWidth = 8;
    const reservedPercentage = auxColWidth + actionsColWidth;
    const availablePercentage = 100 - reservedPercentage;
    const equalWidthPercent = availablePercentage / visibleHeaders.length;

    let styleElement = document.getElementById('primeng-column-redistribution') as HTMLStyleElement;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'primeng-column-redistribution';
      document.head.appendChild(styleElement);
    }

    let cssRules = `
      #${tableId} {
        table-layout: fixed !important;
        width: 100% !important;
      }
    `;

    visibleHeaders.forEach((header) => {
      const columnIndex = Array.from(headerCells).indexOf(header) + 1;

      cssRules += `
        #${tableId} > .p-datatable-thead > tr > th:nth-child(${columnIndex}),
        #${tableId} > .p-datatable-tbody > tr > td:nth-child(${columnIndex}),
        #${tableId} > .p-datatable-tfoot > tr > td:nth-child(${columnIndex}) {
          width: ${equalWidthPercent}% !important;
          max-width: ${equalWidthPercent}% !important;
          min-width: ${equalWidthPercent}% !important;
        }
      `;
    });

    styleElement.textContent = cssRules;
  }

  applyResizeListeners(): void {
    setTimeout(() => {
      const resizers = document.querySelectorAll('.p-column-resizer');
      let tooltipEl = document.getElementById('custom-resize-tooltip') as HTMLElement;
      if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = 'custom-resize-tooltip';
        tooltipEl.classList.add('custom-tooltip');
        tooltipEl.textContent = this.l('DragToResize');
        document.body.appendChild(tooltipEl);
      }

      resizers.forEach((el: HTMLElement) => {
        el.onmouseenter = () => {
          this.isResizing = true;
          const rect = el.getBoundingClientRect();
          tooltipEl.style.top = rect.top - 30 + 'px';
          tooltipEl.style.left = rect.left + 'px';
          tooltipEl.classList.add('visible');
        };

        el.onmouseleave = () => {
          this.isResizing = false;
          tooltipEl.classList.remove('visible');
        };
      });
    }, 0);
  }

  getEventTypeName(event: EventDto): string {
    switch (event.type) {
      case EventTypeEnum._0:
        return this.l('RevocationDate');
      case EventTypeEnum._1:
        return this.l('Reminders');
      case EventTypeEnum._2:
        return this.l('EndDate');
      case EventTypeEnum._3:
        return this.l('GrantDate');
      case EventTypeEnum._4:
        return this.l('EffectiveDate');
      case EventTypeEnum._5:
        return this.l('StartDate');
      default:
        break;
    }

    return '';
  }

  getEventTypeOptions(): { label: string, value: EventTypeEnum }[] {
    return [
      { label: this.l('RevocationDate'), value: EventTypeEnum._0 },
      { label: this.l('Reminders'), value: EventTypeEnum._1 },
      { label: this.l('EndDate'), value: EventTypeEnum._2 },
      { label: this.l('GrantDate'), value: EventTypeEnum._3 },
      { label: this.l('EffectiveDate'), value: EventTypeEnum._4 },
      { label: this.l('StartDate'), value: EventTypeEnum._5 }
    ];
  }

  getOrderDirectionEnum(sortOrder: number): number {
    return sortOrder === 1 ? 1 : 0;
  }

  loadFilterOptions(): void {
    this.loadCompanyOptions();
    this.loadNaturalPersonOptions();
    this.loadJuridicalPersonOptions();
  }

  loadCompanyOptions(): void {
    this._companyService.getAllSelectableCompaniesForUser(
      undefined, // userId
      true, // showAllTenants
      true, // managedCompanies
      undefined, // juridicalPersonId
      'name', // order
      0, // orderDirection
      undefined, // keyword
      undefined, // filters
      0, // skipCount
      1000 // maxResultCount
    ).subscribe(result => {
      this.companyOptions = result.items?.map(company => ({
        label: company.name || '',
        value: company.companyId
      })) || [];
    });
  }

  loadNaturalPersonOptions(): void {
    this._naturalPersonService.getAll(
      undefined, // fullName
      undefined, // phoneNumber
      undefined, // emailAddress
      'fullName', // order
      0, // orderDirection
      undefined, // keyword
      undefined, // filters
      0, // skipCount
      1000 // maxResultCount
    ).subscribe(result => {
      this.naturalPersonOptions = result.items?.map(person => ({
        label: person.fullName || '',
        value: person.id
      })) || [];
    });
  }

  loadJuridicalPersonOptions(): void {
    this._juridicalPersonService.getAll(
      undefined, // name
      undefined, // taxID
      undefined, // emailAddress
      'name', // order
      0, // orderDirection
      undefined, // keyword
      undefined, // filters
      0, // skipCount
      1000 // maxResultCount
    ).subscribe(result => {
      this.juridicalPersonOptions = result.items?.map(person => ({
        label: person.name || '',
        value: person.id
      })) || [];
    });
  }

  get visibleColumns() {
    return this.columns.filter(c => c.visible);
  }

  get minDate(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 3, 1);
  }

  get maxDate(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 3, 0);
  }

  get fromMonthMinDate(): Date {
    return this.minDate;
  }

  get fromMonthMaxDate(): Date {
    return this.toMonth ? new Date(this.toMonth.getFullYear(), this.toMonth.getMonth(), 0) : this.maxDate;
  }

  get toMonthMinDate(): Date {
    return this.fromMonth ? new Date(this.fromMonth.getFullYear(), this.fromMonth.getMonth(), 1) : this.minDate;
  }

  get toMonthMaxDate(): Date {
    return this.maxDate;
  }

  createReminder(event: EventDto): void {
    this._modalService.show(
      CreateOrEditReminderComponent,
      {
        class: 'modal-xm',
        closeInterceptor: () => Promise.reject(),
        initialState: {
          title: this.l('CreateReminder'),
          eventData: {
            companyId: event.companyId,
            juridicalPersonId: event.juridicalPersonId,
            naturalPersonId: event.naturalPersonId,
            date: event.date
          }
        },
      },
      'createReminderFromEvent' + event.id
    );
  }
}
